// Core functionality of redux state is tied to these methods and param reassignment.
// Since Redux is basically legacy code at this point we are grandfathering in these files. The rule will be enforced for any new code committed.
/* eslint-disable no-param-reassign */
import clone from 'rfdc/default';

export default class SearchScopes {
  constructor(galleryState) {
    this.galleryState = galleryState;
    this.currentParams = clone(galleryState.searchParameters);
  }

  handleEvents(newFamily, newAssetType, newParams) {
    // when changing scope to image or film, we'd only want to keep the `events` param
    // when the event has count > 0 of the assettype we're switching to.
    const eventData = this.galleryState.events
      ? this.galleryState.events[0]
      : null;

    if (
      newFamily === 'editorial' &&
      this.currentParams.events &&
      eventData &&
      ((newAssetType === 'film' && eventData.films_count === 0) ||
        (newAssetType === 'image' && eventData.images_count === 0))
    ) {
      delete newParams.events;
    }
  }

  // Both rules answer one question - whether the new scope can keep the sort
  // the old one held - so they sit together rather than either side of the
  // family-correction flag.
  updateSortAfterScopeChange(newFamily, newAssetType, newParams) {
    // when switching to editorial images we want the cookied sort value to be read.
    // When switching out of editorial images we want to go with the default sort.
    if (
      (newFamily === 'editorial' && newAssetType === 'image') ||
      (this.currentParams.family === 'editorial' &&
        this.currentParams.assettype === 'image')
    ) {
      delete newParams.sortorder;
      return;
    }

    // A creative-only scope has no `oldest` sort, but only creative *image* is
    // guarded server-side: SearchSort#valid_sort_for_current_search? gates on
    // `creative_image?` (`image? and creative_only?`), so a carried-over
    // `oldest` is honoured on film and orders the new search oldest-first. The
    // rule above misses that because it only fires when either scope is
    // editorial image. Dropping the sort sends none, which is what the new
    // context resolves its default from - `best`, per
    // default_sort_for_current_search.
    // Creative-and-editorial is deliberately excluded: it is not creative-only,
    // so neither the legacy builder nor SearchSort ever rejected `oldest` there.
    if (newFamily === 'creative' && newParams.sortorder === 'oldest') {
      delete newParams.sortorder;
    }
  }

  updateSearchParametersAfterScopeChange = (newParams) => {
    newParams.page = 1; // any primary context change should start again on page 1
    delete newParams.stackid; // clear stackid (Same Series) param when switching context. Same Series is unlikely to be desired in the new context.
    delete newParams.morelikethis;
    // cookied params - we remove them from state on context switch so that their values can be read and restored from cookies.
    delete newParams.license;
    // Dropping excludenudity hands the new context its own cookied preference:
    // context_preferences.rb registers it for creative_image and
    // creative_image_film only, so any other context falls back to the country
    // default in search_profile.rb#exclude_nudity?. Silence is safe to mean
    // that here because nothing reads it as intent - the switch states its own
    // off value rather than clearing (SwitchFilterConfig's offValue).
    delete newParams.excludenudity;

    const { assettype: newAssetType, family: newFamily } = newParams;
    const isCreativeOrBothFamilies =
      ['creative', 'creative,editorial'].includes(newFamily) || !newFamily;

    // when purposefully switching to creative searches we don't want family corrections to kick in.
    if (isCreativeOrBothFamilies) {
      newParams.suppressfamilycorrection = true;
    }

    this.updateSortAfterScopeChange(newFamily, newAssetType, newParams);
    this.handleEvents(newFamily, newAssetType, newParams);
  };

  isChangingScope = (newParams) =>
    !(
      newParams.family === this.currentParams.family &&
      newParams.assettype === this.currentParams.assettype &&
      newParams.editorialproducts === this.currentParams.editorialproducts
    );

  applyStateUpdates = (newState) => {
    if (!this.isChangingScope(newState.searchParameters)) {
      return newState;
    }

    this.updateSearchParametersAfterScopeChange(newState.searchParameters);
    return newState;
  };
}
